/**
 * AI 타임아웃 관리 (옵션 3 하이브리드).
 *  - 동적: admin 이 직접 제어(아이콘 생성/외부테스트/provider테스트) — 저장 즉시 반영(재시작 0).
 *  - env: orchestrator/backend/chat-server 핵심 타임아웃 — 값 편집(.env 기록) + 재시작 안내.
 */
import { useCallback, useEffect, useState } from 'react';
import './ServiceModules.css';
import { Save, AlertTriangle } from '../../../icons';
import {
  listAiTimeouts, setDynamicTimeout, setEnvTimeout,
  type DynamicTimeout, type EnvTimeout,
} from '../../../api/aiTimeouts';
import { ConsoleScreenHead } from '../../../components/console-list/ConsoleScreenHead';

const fmtSec = (ms: number) => `${(ms / 1000).toLocaleString()}초`;

function MsField({ valueMs, onSave, busy }: { valueMs: number; onSave: (ms: number) => void; busy: boolean }) {
  const [sec, setSec] = useState(String(Math.round(valueMs / 1000)));
  useEffect(() => { setSec(String(Math.round(valueMs / 1000))); }, [valueMs]);
  const n = Number(sec);
  const dirty = Number.isFinite(n) && n > 0 && n * 1000 !== valueMs;
  return (
    <div className="svc-tmo__field">
      <input type="number" min={1} value={sec} onChange={(e) => setSec(e.target.value)} aria-label="초" />
      <span className="svc-tmo__unit">초</span>
      <button type="button" className="svc-btn" disabled={!dirty || busy} onClick={() => onSave(Math.round(n * 1000))}>
        <Save size={13} /> 저장
      </button>
    </div>
  );
}

export function AiTimeoutSection() {
  const [dynamic, setDynamic] = useState<DynamicTimeout[]>([]);
  const [env, setEnv] = useState<EnvTimeout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyKey, setBusyKey] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);

  const reload = useCallback(async () => {
    setLoading(true); setError(null);
    try { const d = await listAiTimeouts(); setDynamic(d.dynamic); setEnv(d.env); }
    catch (e: any) { setError(e?.response?.data?.error ?? e?.message ?? '불러오기 실패'); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { void reload(); }, [reload]);

  async function saveDynamic(key: string, ms: number) {
    setBusyKey(key); setError(null); setNotice(null);
    try { await setDynamicTimeout(key, ms); setNotice(`적용됨(즉시): ${key} = ${fmtSec(ms)}`); await reload(); }
    catch (e: any) { setError(e?.response?.data?.error ?? '저장 실패'); }
    finally { setBusyKey(null); }
  }
  async function saveEnv(key: string, ms: number) {
    setBusyKey(key); setError(null); setNotice(null);
    try {
      const r = await setEnvTimeout(key, ms);
      setNotice(`저장됨: ${key} = ${fmtSec(ms)} — 반영하려면 "${r.restart_service}" 재시작이 필요합니다.`);
      await reload();
    } catch (e: any) { setError(e?.response?.data?.error ?? '저장 실패'); }
    finally { setBusyKey(null); }
  }

  return (
    <section className="svc-cat svc-cat--tmo">
      <ConsoleScreenHead
        title="AI 타임아웃"
        kpi={`${dynamic.length + env.length}개`}
        desc="AI 요청 대기 시간 설정 — 동적은 즉시, env 는 재시작 후 반영됩니다."
      />

      {error && <div className="admin-console__alert admin-console__alert--warn" role="alert">{error}</div>}
      {notice && <div className="svc-tmo__notice" role="status">{notice}</div>}

      {loading ? (
        <p className="svc-empty">불러오는 중…</p>
      ) : (
        <>
          {/* 동적 — 즉시 반영 */}
          <h4 className="svc-tmo__group">즉시 적용 (재시작 불필요)</h4>
          <div className="svc-grid">
            {dynamic.map((d) => (
              <div key={d.key} className="svc-card">
                <div className="svc-card__top">
                  <span className="svc-card__name" title={d.key}>{d.label}</span>
                  <span className="svc-card__chip">기본 {fmtSec(d.default_ms)}</span>
                </div>
                <div className="svc-card__detail" style={{ fontFamily: 'inherit' }}>{d.desc}</div>
                <MsField valueMs={d.value_ms} busy={busyKey === d.key} onSave={(ms) => void saveDynamic(d.key, ms)} />
              </div>
            ))}
          </div>

          {/* env — 재시작 필요 */}
          <h4 className="svc-tmo__group" style={{ marginTop: 18 }}>
            <AlertTriangle size={14} /> 서비스 설정 (변경 시 해당 서비스 재시작 필요)
          </h4>
          <div className="svc-grid">
            {env.map((e) => (
              <div key={e.key} className="svc-card">
                <div className="svc-card__top">
                  <span className="svc-card__name" title={e.key}>{e.label}</span>
                  <span className="svc-card__chip">{e.service}</span>
                </div>
                <div className="svc-card__detail" style={{ fontFamily: 'inherit' }}>{e.desc}</div>
                <div className="svc-card__chips">
                  <span className="svc-card__chip">현재 {e.current_ms == null ? `기본(${fmtSec(e.default_ms)})` : fmtSec(e.current_ms)}</span>
                  <span className="svc-card__chip">적용중 {fmtSec(e.effective_ms)}</span>
                </div>
                <MsField valueMs={e.effective_ms} busy={busyKey === e.key} onSave={(ms) => void saveEnv(e.key, ms)} />
                <div className="svc-tmo__restart">재시작 대상: <code>{e.restart_service}</code></div>
              </div>
            ))}
          </div>
          <p className="svc-tmo__hint">
            env 항목은 값 저장 후 해당 서비스를 재시작해야 반영됩니다(전체 사용자 순단 주의).
            예: <code>systemctl restart &lt;서비스&gt;</code>
          </p>
        </>
      )}
    </section>
  );
}
